/* eslint-disable no-console */
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { api } from '../../../../config';
import {
  DYED_UNDYED_DEBOUNCE_MS,
  EMPTY_COUNTER_FORM,
  getAlphaColumnLabel,
} from '../../../../shared/offer-domain/constants';
import { FOB_INCOTERM_ID } from '../../components/ModelDelivery/shared/constants';
import {
  unwrapApiItems,
  unwrapApiData,
  getRouteIdByIncoterm,
  getListData,
  mapDeliveryDetailRow,
  mapIncotermDetailToCounterForm,
  preserveProcurementIncotermFields,
  buildCounterFormFromRow,
  applyCounterQuantityChange,
  enrichDeliveryItemsWithFobLocations,
  enrichDeliveryItemsWithCounterOffers,
  hasCounterOffer,
  buildCounterSavePayloads,
  validateCounterIncoterm,
  splitDeliveryBasisByMultiport,
  hasCounterFormChanges,
  fetchProductTypePricingUnit,
  parseNumericQuantity,
  isFobOriginItem,
  indexParcelFobLocationsByDetail,
  mergeParcelFobOriginPricing,
  expandFobOriginCapableRows,
  rebuildFobOriginCapableRows,
} from '../../components/ModelDelivery/shared/utils';
import { getRowKey, collectOriginalCargoRatios } from './shared';
import { isOfferDeliveryEditable } from '../../utils/offerEditAccess';
import {
  putCargoRatioPricing,
  resolveLpgSourceParcelSize,
} from '../../components/ModelDelivery/shared/cargoRatioUtils';
import {
  putCargoRatioCounterPricing,
  resolveCargoRatioExistingFromRow,
} from '../../../../shared/offer-domain/cargoRatio';
import { getCargoRatioKey } from '../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';

const isPricingValueFilled = value =>
  value !== '' && value !== null && value !== undefined;

const useModelDeliveryTableBase = (
  { form, formMain, quantityDeliveryTermId, token, onDataChange },
  strategy
) => {
  const [qtyModeDeliveryIncoterm, setQtyModeDeliveryIncoterm] = useState({
    items: [],
  });
  const [currentPage] = useState(1);
  const [rowsPerPage] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [capableState, setCapableState] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [counterFormData, setCounterFormData] = useState(EMPTY_COUNTER_FORM);
  const [counterOriginalFormData, setCounterOriginalFormData] = useState(null);
  const [counterFormErrors, setCounterFormErrors] = useState({});
  const [counterFormLoading, setCounterFormLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(
    Boolean(form?.id && quantityDeliveryTermId)
  );
  const [incotermTypeOptions, setIncotermTypeOptions] = useState([]);
  const [incotermRouteOptions, setIncotermRouteOptions] = useState([]);
  const [incotermLocationOptions, setIncotermLocationOptions] = useState([]);
  const dyedUndyedTimersRef = useRef({});
  const pendingCounterPricingRef = useRef({});
  const pendingSourcePricingRef = useRef({});
  const pricingSaveEpochRef = useRef({});
  const locationFetchSeqRef = useRef(0);

  const isLpgProduct = strategy.isLpg;
  const usesDyedUndyed = strategy.usesDyedUndyedAlpha(form?.productNameId);
  const usesAlphaField = strategy.getUsesAlphaField(form);
  const [pricingUnit, setPricingUnit] = useState(null);
  const requiresCargoRatio = strategy.getRequiresCargoRatio(form);
  const isDeliveryEditable = isOfferDeliveryEditable(formMain);
  const alphaColumnLabel = getAlphaColumnLabel(pricingUnit);
  const counterHasChanges = useMemo(
    () =>
      hasCounterFormChanges(counterFormData, counterOriginalFormData, {
        ...strategy.getCounterFormChangeOptions(requiresCargoRatio),
      }),
    [counterFormData, counterOriginalFormData, requiresCargoRatio, strategy]
  );

  useEffect(() => {
    if (!strategy.shouldFetchPricingUnit(form)) {
      setPricingUnit(null);
      return;
    }
    const productTypeId =
      form?.productTypeId ?? form?.productType?.id ?? form?.productType?.Id;
    let cancelled = false;
    fetchProductTypePricingUnit(productTypeId, token).then(unit => {
      if (!cancelled) setPricingUnit(unit);
    });
    return () => {
      cancelled = true;
    };
  }, [
    form,
    strategy,
    form?.productTypeId,
    form?.productType?.id,
    form?.productType?.Id,
    token,
  ]);

  useEffect(() => {
    const timers = dyedUndyedTimersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const fetchDeliveryDataDetailIncoterm = useCallback(
    async (procurementId, termId, page, pageSize, authToken) => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${api.API_URL}/procurement/get-delivery-detail?PageNumber=${page}&PageSize=${pageSize}&procurementId=${procurementId}&quantityDeliveryTermId=${termId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const groups = result?.data?.data || result?.data?.Data || [];
        let items = groups.flatMap(item =>
          getListData(item).map(details => mapDeliveryDetailRow(item, details))
        );

        try {
          items = await enrichDeliveryItemsWithFobLocations({
            quantityDeliveryTermId: termId,
            token: authToken,
            items,
          });
        } catch (fobError) {
          console.error('Error enriching FOB locations:', fobError);
        }

        const hasFobOrigin = items.some(row => isFobOriginItem(row));
        if (hasFobOrigin && procurementId) {
          try {
            const parcelResponse = await fetch(
              `${api.API_URL}/procurement/get-delivery-parcel?PageNumber=1&PageSize=100&procurementId=${procurementId}`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
              }
            );
            if (parcelResponse.ok) {
              const parcelResult = await parcelResponse.json();
              const parcelGroups =
                parcelResult?.data?.data || parcelResult?.data?.Data || [];
              const parcelByDetail =
                indexParcelFobLocationsByDetail(parcelGroups);
              items = mergeParcelFobOriginPricing(items, parcelByDetail);
            }
          } catch (parcelError) {
            console.error('Error enriching FOB Origin parcel:', parcelError);
          }
        }

        items = expandFobOriginCapableRows(items);

        items = await strategy.enrichAfterFob({
          items,
          procurementId,
          quantityDeliveryTermId: termId,
          requiresCargoRatio,
        });

        try {
          items = await enrichDeliveryItemsWithCounterOffers({
            quantityDeliveryTermId: termId,
            token: authToken,
            items,
            incotermTypeOptions,
            incotermRouteOptions,
            requireIncotermCounter: strategy.requireIncotermCounter,
          });
        } catch (counterError) {
          console.error('Error enriching counter offers:', counterError);
        }

        items = items.map(row => {
          if (!hasCounterOffer(row) && row.isCapable !== false) {
            return row;
          }
          if (usesAlphaField) {
            const counterAlpha =
              row.counterAlpha !== null &&
              row.counterAlpha !== undefined &&
              String(row.counterAlpha).trim() !== '' &&
              Number(row.counterAlpha) !== 0
                ? row.counterAlpha
                : row.alpha !== null &&
                    row.alpha !== undefined &&
                    String(row.alpha).trim() !== '' &&
                    Number(row.alpha) !== 0
                  ? row.alpha
                  : '';
            return {
              ...row,
              capable: false,
              counterAlpha,
              alpha: '',
            };
          }
          return {
            ...row,
            capable: false,
            alpha: '',
            counterDyed:
              row.counterDyed !== null &&
              row.counterDyed !== undefined &&
              String(row.counterDyed).trim() !== ''
                ? row.counterDyed
                : (row.dyed ?? row.unitBbl ?? ''),
            counterUndyed:
              row.counterUndyed !== null &&
              row.counterUndyed !== undefined &&
              String(row.counterUndyed).trim() !== ''
                ? row.counterUndyed
                : (row.undyed ?? row.cbm ?? ''),
            cbm: '',
            unitBbl: '',
            dyed: '',
            undyed: '',
          };
        });

        setQtyModeDeliveryIncoterm({ items });

        setExpandedRows(() => {
          const next = {};
          items.forEach(row => {
            if (!hasCounterOffer(row)) return;
            const expandKey = strategy.getExpandKey(row);
            next[expandKey] = true;
          });
          return next;
        });

        setCapableState(prev => {
          const nextCapable = { ...prev };
          items.forEach(row => {
            const rowKey = getRowKey(row);
            if (hasCounterOffer(row)) {
              nextCapable[rowKey] = false;
              return;
            }
            if (requiresCargoRatio) {
              nextCapable[rowKey] =
                row.alpha !== null &&
                row.alpha !== undefined &&
                String(row.alpha).trim() !== '';
              return;
            }
            if (row.isCapable === true || row.isCapable === false) {
              nextCapable[rowKey] = row.isCapable;
            } else if (!(rowKey in nextCapable)) {
              if (usesAlphaField) {
                nextCapable[rowKey] = !!(row.alpha > 0);
              } else {
                nextCapable[rowKey] = !!(row.unitBbl > 0 || row.cbm > 0);
              }
            }
          });

          const capableByDetail = new Map();
          if (!requiresCargoRatio) {
            items.forEach(row => {
              const detailId = String(row.quantityInformationDetailId ?? '');
              if (!detailId) return;
              const rowKey = getRowKey(row);
              const capable = !!nextCapable[rowKey];
              if (!capableByDetail.has(detailId)) {
                capableByDetail.set(detailId, capable);
                return;
              }
              if (row.isCapable === true || row.isCapable === false) {
                capableByDetail.set(detailId, row.isCapable);
              } else if (capable) {
                capableByDetail.set(detailId, true);
              }
            });

            items.forEach(row => {
              const detailId = String(row.quantityInformationDetailId ?? '');
              if (!detailId || !capableByDetail.has(detailId)) return;
              nextCapable[getRowKey(row)] = capableByDetail.get(detailId);
            });
          }

          return nextCapable;
        });
      } catch (error) {
        console.error('Error in fetchDeliveryDataDetailIncoterm:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      strategy,
      usesAlphaField,
      requiresCargoRatio,
      incotermTypeOptions,
      incotermRouteOptions,
    ]
  );

  useEffect(() => {
    if (!form?.id) return;
    fetchDeliveryDataDetailIncoterm(
      form.id,
      quantityDeliveryTermId,
      currentPage,
      rowsPerPage,
      token
    );
  }, [
    form?.id,
    quantityDeliveryTermId,
    currentPage,
    rowsPerPage,
    token,
    fetchDeliveryDataDetailIncoterm,
  ]);

  useEffect(() => {
    if (onDataChange && qtyModeDeliveryIncoterm.items.length > 0) {
      const dataWithCapableStatus = qtyModeDeliveryIncoterm.items.map(item => ({
        ...item,
        capable: capableState[getRowKey(item)] || false,
      }));
      onDataChange(quantityDeliveryTermId, dataWithCapableStatus);
    }
  }, [
    qtyModeDeliveryIncoterm.items,
    capableState,
    onDataChange,
    quantityDeliveryTermId,
  ]);

  const fetchIncotermTypeOptions = useCallback(async () => {
    try {
      const response = await fetch(`${api.API_URL}/incoterm`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      const options = unwrapApiItems(result).map(item => ({
        value: item.id,
        label: item.name,
      }));
      setIncotermTypeOptions(options);
    } catch (error) {
      console.error('Error fetching incoterm type:', error);
    }
  }, [token]);

  const fetchIncotermRouteOptions = useCallback(async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/incoterm/get-incoterm-route`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      const options = unwrapApiItems(result).map(item => ({
        value: item.id,
        label: item.name,
      }));
      setIncotermRouteOptions(options);
    } catch (error) {
      console.error('Error fetching incoterm route:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchIncotermTypeOptions();
    fetchIncotermRouteOptions();
  }, [token, fetchIncotermTypeOptions, fetchIncotermRouteOptions]);

  const fetchIncotermLocationOptions = useCallback(
    async (route = null, incotermId = null) => {
      const routeId =
        route !== null && route !== undefined && route !== ''
          ? route
          : getRouteIdByIncoterm(route, incotermId);

      if (!routeId) {
        setIncotermLocationOptions([]);
        return [];
      }

      const seq = ++locationFetchSeqRef.current;
      setIncotermLocationOptions([]);

      try {
        const response = await fetch(
          `${api.API_URL}/incoterm/get-incoterm-location?IncotermRouteId=${routeId}&IsLpgCase=${strategy.isLpgCaseForLocations}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        const rawItems =
          unwrapApiItems(result).length > 0
            ? unwrapApiItems(result)
            : (result?.items ?? result?.Items ?? []);
        const options = (Array.isArray(rawItems) ? rawItems : []).map(item => ({
          value: item.id ?? item.Id,
          label: item.name ?? item.Name,
        }));

        if (seq === locationFetchSeqRef.current) {
          setIncotermLocationOptions(options);
        }
        return options;
      } catch (error) {
        console.error('Error fetching incoterm location:', error);
        if (seq === locationFetchSeqRef.current) {
          setIncotermLocationOptions([]);
        }
        return [];
      }
    },
    [token, strategy]
  );

  useEffect(() => {
    if (!counterModalOpen) return;

    if (counterFormData.incotermId === FOB_INCOTERM_ID) {
      if (counterFormData.incotermRouteId) {
        fetchIncotermLocationOptions(counterFormData.incotermRouteId, null);
      }
      return;
    }

    if (counterFormData.incotermId) {
      fetchIncotermLocationOptions(null, counterFormData.incotermId);
    }
  }, [
    counterModalOpen,
    counterFormData.incotermId,
    counterFormData.incotermRouteId,
    fetchIncotermLocationOptions,
  ]);

  const fetchQuantityIncotermDetail = useCallback(
    async quantityIncotermId => {
      const response = await fetch(
        `${api.API_URL}/quantity-incoterm/${quantityIncotermId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      return unwrapApiData(result);
    },
    [token]
  );

  const filteredItems = useMemo(() => {
    if (!searchTerm) return qtyModeDeliveryIncoterm.items;
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return qtyModeDeliveryIncoterm.items.filter(item =>
      Object.values(item).some(value => {
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(lowercasedSearchTerm);
      })
    );
  }, [qtyModeDeliveryIncoterm.items, searchTerm]);

  const fobRouteOptions = useMemo(
    () =>
      incotermRouteOptions.filter(
        option =>
          option.label !== 'N/A' && ['Straits', 'Origin'].includes(option.label)
      ),
    [incotermRouteOptions]
  );

  const postCheckIsCapable = async (quantityInformationDetailId, isCapable) => {
    const params = new URLSearchParams({
      quantityInformationDetailId,
      isCapable: String(isCapable),
    });
    const response = await fetch(
      `${api.API_URL}/dmut/check-iscapable?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const putIsCapableLocations = async (
    quantityInformationDetailId,
    incotermLocationIds = []
  ) => {
    if (!quantityInformationDetailId) return null;

    const response = await fetch(
      `${api.API_URL}/q-information-detail/${quantityInformationDetailId}/is-capable-locations`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          incotermLocationIds: Array.isArray(incotermLocationIds)
            ? incotermLocationIds
            : [],
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const postSetDyedUndyed = async item => {
    const toPayloadValue = value => {
      if (value === '' || value === undefined) return null;
      return value;
    };

    const payload = {
      listDyedUndyedAlpha: [
        {
          quantityInformationDetailId: item.quantityInformationDetailId,
          dyed: toPayloadValue(item.dyed),
          undyed: toPayloadValue(item.undyed),
          dyed2: toPayloadValue(item.dyed2 ?? item.counterDyed),
          undyed2: toPayloadValue(item.undyed2 ?? item.counterUndyed),
          dyed3: toPayloadValue(item.dyed3),
          undyed3: toPayloadValue(item.undyed3),
          alpha: toPayloadValue(item.alpha),
          alpha2: toPayloadValue(item.alpha2),
          alpha3: toPayloadValue(item.alpha3),
        },
      ],
    };

    const response = await fetch(`${api.API_URL}/dmut/set-dyed-undyed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json-patch+json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const postSetDyedUndyedFob = async item => {
    const toPayloadValue = value => {
      if (value === '' || value === undefined) return null;
      return value;
    };

    const payload = {
      listDyedUndyedAlphaFob: [
        {
          quantityInformationDetailId: item.quantityInformationDetailId,
          incotermLocationId: item.incotermLocationId,
          locationSlot: item.locationSlot ?? null,
          dyed: toPayloadValue(item.dyed),
          undyed: toPayloadValue(item.undyed),
          dyed2: toPayloadValue(item.dyed2 ?? item.counterDyed),
          undyed2: toPayloadValue(item.undyed2 ?? item.counterUndyed),
          dyed3: toPayloadValue(item.dyed3),
          undyed3: toPayloadValue(item.undyed3),
          alpha: toPayloadValue(item.alpha),
          alpha2: toPayloadValue(item.alpha2),
          alpha3: toPayloadValue(item.alpha3),
        },
      ],
    };

    const response = await fetch(`${api.API_URL}/dmut/set-dyed-undyed-fob`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json-patch+json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  const saveSourcePricing = async item => {
    if (item?.incotermLocationId && isFobOriginItem(item)) {
      return postSetDyedUndyedFob(item);
    }
    return postSetDyedUndyed(item);
  };

  const clearSourcePricing = async quantityInformationDetailId => {
    if (!quantityInformationDetailId) return null;
    return postSetDyedUndyed({
      quantityInformationDetailId,
      dyed: null,
      undyed: null,
      dyed2: null,
      undyed2: null,
      dyed3: null,
      undyed3: null,
      alpha: null,
      alpha2: null,
      alpha3: null,
    });
  };

  const clearFobOriginLocationPricing = async rows => {
    const locationRows = (Array.isArray(rows) ? rows : []).filter(
      row => row?.incotermLocationId && isFobOriginItem(row)
    );
    if (!locationRows.length) return null;
    await Promise.all(
      locationRows.map(row =>
        postSetDyedUndyedFob({
          quantityInformationDetailId: row.quantityInformationDetailId,
          incotermLocationId: row.incotermLocationId,
          locationSlot: row.locationSlot ?? null,
          dyed: null,
          undyed: null,
          dyed2: null,
          undyed2: null,
          dyed3: null,
          undyed3: null,
          alpha: null,
          alpha2: null,
          alpha3: null,
        }).catch(() => null)
      )
    );
    return null;
  };

  const invalidateSourcePricingSaves = (
    quantityInformationDetailId,
    syncKeys
  ) => {
    const detailId = String(quantityInformationDetailId ?? '');
    if (detailId) {
      pricingSaveEpochRef.current[detailId] =
        (pricingSaveEpochRef.current[detailId] || 0) + 1;
    }

    (syncKeys || []).forEach(key => {
      Object.keys(dyedUndyedTimersRef.current).forEach(timerKey => {
        if (timerKey !== key && !timerKey.startsWith(`${key}-counter-`)) {
          return;
        }
        clearTimeout(dyedUndyedTimersRef.current[timerKey]);
        delete dyedUndyedTimersRef.current[timerKey];
        delete pendingCounterPricingRef.current[timerKey];
      });
      delete pendingSourcePricingRef.current[key];
    });
  };

  const scheduleDyedUndyedSave = item => {
    const rowKey = getRowKey(item);
    const detailId = String(item.quantityInformationDetailId ?? '');
    const epoch = pricingSaveEpochRef.current[detailId] || 0;

    if (dyedUndyedTimersRef.current[rowKey]) {
      clearTimeout(dyedUndyedTimersRef.current[rowKey]);
    }

    pendingSourcePricingRef.current[rowKey] = item;

    dyedUndyedTimersRef.current[rowKey] = setTimeout(async () => {
      delete pendingSourcePricingRef.current[rowKey];
      if ((pricingSaveEpochRef.current[detailId] || 0) !== epoch) return;

      try {
        await saveSourcePricing(item);
      } catch (error) {
        console.error('Error in postSetDyedUndyed:', error);
      }
    }, DYED_UNDYED_DEBOUNCE_MS);
  };

  const scheduleCargoRatioPricingSave = item => {
    const rowKey = getRowKey(item);
    const cargoRatioId = String(item.quantityInformationCargoRatioId ?? '');
    if (!cargoRatioId) return;

    const epochKey = `cr-${cargoRatioId}`;
    pricingSaveEpochRef.current[epochKey] =
      pricingSaveEpochRef.current[epochKey] || 0;
    const epoch = pricingSaveEpochRef.current[epochKey];

    if (dyedUndyedTimersRef.current[rowKey]) {
      clearTimeout(dyedUndyedTimersRef.current[rowKey]);
    }

    pendingSourcePricingRef.current[rowKey] = {
      ...item,
      __cargoRatioPricing: true,
    };

    dyedUndyedTimersRef.current[rowKey] = setTimeout(async () => {
      delete pendingSourcePricingRef.current[rowKey];
      if ((pricingSaveEpochRef.current[epochKey] || 0) !== epoch) return;

      try {
        await putCargoRatioPricing({
          quantityInformationCargoRatioId: item.quantityInformationCargoRatioId,
          quantityInformationId: item.quantityInformationId,
          quantityIncotermFK: item.quantityIncotermId ?? null,
          cargoRatio: item.cargoRatio,
          parcelSize: item.cargoRatioParcelSize ?? item.parcelSize ?? null,
          alpha: item.alpha ?? null,
          existing: resolveCargoRatioExistingFromRow(item),
          token,
        });
      } catch (error) {
        console.error('Error in putCargoRatioPricing:', error);
      }
    }, DYED_UNDYED_DEBOUNCE_MS);
  };

  const scheduleCounterCargoRatioAlphaSave = item => {
    const ratioRecordId =
      item.counterCargoRatioRecordId ??
      item.counterCargoRatios?.find(
        ratio =>
          getCargoRatioKey(ratio.id) ===
          getCargoRatioKey(item.counterCargoRatioId ?? item.cargoRatio)
      )?.recordId ??
      null;
    const timerKey = [
      getRowKey(item),
      'counter-cr',
      ratioRecordId || item.quantityInformationCargoRatioId || 'none',
    ].join('-');

    if (dyedUndyedTimersRef.current[timerKey]) {
      clearTimeout(dyedUndyedTimersRef.current[timerKey]);
    }

    pendingCounterPricingRef.current[timerKey] = {
      ...item,
      __counterCargoRatioPricing: true,
      counterCargoRatioRecordId: ratioRecordId,
    };

    dyedUndyedTimersRef.current[timerKey] = setTimeout(async () => {
      delete pendingCounterPricingRef.current[timerKey];
      try {
        if (!ratioRecordId) return;
        const sourceParcelSize = resolveLpgSourceParcelSize(item);
        const existing = resolveCargoRatioExistingFromRow({
          ...item,
          counterCargoRatioRecordId: ratioRecordId,
          cargoRatioSnapshot: item.counterCargoRatioSnapshot ??
            item.cargoRatioSnapshot ?? {
              id: ratioRecordId,
              quantityInformationCargoRatioFK:
                item.quantityInformationCargoRatioId,
              quantityInformationCounterFK: item.quantityInformationCounterId,
              quantityIncotermCounterFK: item.quantityIncotermCounterId ?? null,
              cargoRatio: item.counterCargoRatioId ?? item.cargoRatio,
              parcelSize: sourceParcelSize,
              alpha: item.counterAlpha ?? null,
              alphaImprovement: item.cargoRatioAlphaImprovement ?? null,
              alphaHps: item.alphaHps ?? null,
              alphaDyedHps: item.alphaDyedHps ?? null,
              alphaUndyedHps: item.alphaUndyedHps ?? null,
            },
        });
        await putCargoRatioCounterPricing({
          quantityInformationCargoRatioCounterId: ratioRecordId,
          existing,
          patch: {
            cargoRatio: Number(
              getCargoRatioKey(item.counterCargoRatioId ?? item.cargoRatio)
            ),
            quantityInformationCargoRatioFK:
              item.quantityInformationCargoRatioId,
            quantityInformationCounterFK: item.quantityInformationCounterId,
            quantityIncotermCounterFK: item.quantityIncotermCounterId ?? null,
            parcelSize: sourceParcelSize,
            alpha: item.counterAlpha ?? null,
          },
          token,
        });
      } catch (error) {
        console.error('Error in counter cargo ratio alpha save:', error);
      }
    }, DYED_UNDYED_DEBOUNCE_MS);
  };

  const runCounterPricingSave = async item => {
    if (!item.quantityInformationDetailId) return;

    const hasCounterValue = usesAlphaField
      ? isPricingValueFilled(item.counterAlpha)
      : isPricingValueFilled(item.counterDyed) ||
        isPricingValueFilled(item.counterUndyed);

    if (
      hasCounterValue &&
      !item.quantityInformationDetailCounterId &&
      item.quantityIncotermCounterId &&
      item.quantityInformationCounterId
    ) {
      try {
        await fetch(`${api.API_URL}/q-information-detail-counter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            quantityInformationDetailFK: item.quantityInformationDetailId,
            quantityInformationCounterId: item.quantityInformationCounterId,
            quantityIncotermCounterId: item.quantityIncotermCounterId,
          }),
        });
      } catch (error) {
        console.error('Error creating missing detail-counter:', error);
      }
    }

    await postSetDyedUndyed(
      usesAlphaField
        ? {
            quantityInformationDetailId: item.quantityInformationDetailId,
            dyed: null,
            undyed: null,
            dyed2: null,
            undyed2: null,
            dyed3: null,
            undyed3: null,
            alpha: item.counterAlpha ?? null,
            alpha2: null,
            alpha3: null,
          }
        : {
            quantityInformationDetailId: item.quantityInformationDetailId,
            dyed: item.counterDyed ?? item.dyed2 ?? null,
            undyed: item.counterUndyed ?? item.undyed2 ?? null,
            dyed2: null,
            undyed2: null,
            dyed3: null,
            undyed3: null,
            alpha: null,
            alpha2: null,
            alpha3: null,
          }
    );
  };

  const scheduleCounterDyedUndyedSave = item => {
    const timerKey = [
      getRowKey(item),
      'counter',
      item.quantityIncotermCounterId || item.quantityIncotermId || 'none',
      item.quantityInformationDetailId || 'none',
    ].join('-');
    if (dyedUndyedTimersRef.current[timerKey]) {
      clearTimeout(dyedUndyedTimersRef.current[timerKey]);
    }

    pendingCounterPricingRef.current[timerKey] = item;

    dyedUndyedTimersRef.current[timerKey] = setTimeout(async () => {
      delete pendingCounterPricingRef.current[timerKey];
      try {
        await runCounterPricingSave(item);
      } catch (error) {
        console.error('Error in runCounterPricingSave:', error);
      }
    }, DYED_UNDYED_DEBOUNCE_MS);
  };

  const flushPendingPricingSavesRef = useRef(() => {});

  const flushPendingPricingSaves = () => {
    const timers = dyedUndyedTimersRef.current;
    Object.keys(timers).forEach(key => {
      if (timers[key]) {
        clearTimeout(timers[key]);
        delete timers[key];
      }
    });

    const pendingSource = { ...pendingSourcePricingRef.current };
    pendingSourcePricingRef.current = {};
    Object.values(pendingSource).forEach(item => {
      if (item?.__cargoRatioPricing) {
        putCargoRatioPricing({
          quantityInformationCargoRatioId: item.quantityInformationCargoRatioId,
          quantityInformationId: item.quantityInformationId,
          quantityIncotermFK: item.quantityIncotermId ?? null,
          cargoRatio: item.cargoRatio,
          parcelSize: item.cargoRatioParcelSize ?? item.parcelSize ?? null,
          alpha: item.alpha ?? null,
          existing: resolveCargoRatioExistingFromRow(item),
          token,
        }).catch(error => {
          console.error('Error flushing cargo ratio pricing save:', error);
        });
        return;
      }
      postSetDyedUndyed(item).catch(error => {
        console.error('Error flushing source pricing save:', error);
      });
    });

    const pending = { ...pendingCounterPricingRef.current };
    pendingCounterPricingRef.current = {};
    Object.values(pending).forEach(item => {
      if (item?.__counterCargoRatioPricing) {
        const ratioRecordId = item.counterCargoRatioRecordId;
        if (!ratioRecordId) return;
        const sourceParcelSize = resolveLpgSourceParcelSize(item);
        putCargoRatioCounterPricing({
          quantityInformationCargoRatioCounterId: ratioRecordId,
          existing: resolveCargoRatioExistingFromRow({
            ...item,
            counterCargoRatioRecordId: ratioRecordId,
          }),
          patch: {
            cargoRatio: Number(
              getCargoRatioKey(item.counterCargoRatioId ?? item.cargoRatio)
            ),
            quantityInformationCargoRatioFK:
              item.quantityInformationCargoRatioId,
            quantityInformationCounterFK: item.quantityInformationCounterId,
            quantityIncotermCounterFK: item.quantityIncotermCounterId ?? null,
            parcelSize: sourceParcelSize,
            alpha: item.counterAlpha ?? null,
          },
          token,
        }).catch(error => {
          console.error('Error flushing counter cargo ratio pricing:', error);
        });
        return;
      }
      runCounterPricingSave(item).catch(error => {
        console.error('Error flushing counter pricing save:', error);
      });
    });
  };
  flushPendingPricingSavesRef.current = flushPendingPricingSaves;

  useEffect(
    () => () => {
      flushPendingPricingSavesRef.current();
    },
    []
  );

  const handleInputChange = (e, rowKey, field, type, row) => {
    const rawValue = e.target.value;
    const value =
      type === 'number'
        ? rawValue === '' || rawValue === null || rawValue === undefined
          ? ''
          : Number.isFinite(parseFloat(rawValue))
            ? parseFloat(rawValue)
            : ''
        : rawValue;

    const updatedDyed = field === 'unitBbl' ? value : row.dyed;
    const updatedUndyed = field === 'cbm' ? value : row.undyed;
    const updatedAlpha = field === 'alpha' ? value : row.alpha;
    const updatedCounterDyed =
      field === 'counterDyed' ? value : row.counterDyed;
    const updatedCounterUndyed =
      field === 'counterUndyed' ? value : row.counterUndyed;
    const updatedCounterAlpha =
      field === 'counterAlpha' ? value : row.counterAlpha;
    const isSourcePricingField =
      field === 'alpha' ||
      field === 'cbm' ||
      field === 'unitBbl' ||
      field === 'dyed' ||
      field === 'undyed';
    const isCounterPricingField =
      field === 'counterAlpha' ||
      field === 'counterDyed' ||
      field === 'counterUndyed';
    const detailId = String(row?.quantityInformationDetailId ?? '');
    const counterIncotermId = String(row?.quantityIncotermId ?? '');
    const counterIncotermCounterId = String(
      row?.quantityIncotermCounterId ?? ''
    );
    const isolateCargoRatioRows = requiresCargoRatio;
    const isolateFobOriginLocation =
      isSourcePricingField && !!row?.incotermLocationId && isFobOriginItem(row);

    setQtyModeDeliveryIncoterm(prev => {
      const newItems = prev.items.map(item => {
        const sameDetailPricing =
          !isolateCargoRatioRows &&
          !isolateFobOriginLocation &&
          isSourcePricingField &&
          detailId &&
          String(item.quantityInformationDetailId ?? '') === detailId;
        const sameCounterIncoterm = counterIncotermCounterId
          ? String(item.quantityIncotermCounterId ?? '') ===
            counterIncotermCounterId
          : String(item.quantityIncotermId ?? '') === counterIncotermId;
        const sameCounterDetailPricing =
          !isolateCargoRatioRows &&
          isCounterPricingField &&
          !!detailId &&
          String(item.quantityInformationDetailId ?? '') === detailId &&
          sameCounterIncoterm &&
          hasCounterOffer(item);
        const isTargetRow =
          sameDetailPricing ||
          sameCounterDetailPricing ||
          getRowKey(item) === rowKey;

        if (!isTargetRow) {
          return item;
        }

        const updatedItem = sameDetailPricing
          ? {
              ...item,
              ...(field === 'alpha' ? { alpha: value } : null),
              ...(field === 'cbm' ? { cbm: value, undyed: value } : null),
              ...(field === 'unitBbl' ? { unitBbl: value, dyed: value } : null),
              ...(field === 'dyed' ? { dyed: value, unitBbl: value } : null),
              ...(field === 'undyed' ? { undyed: value, cbm: value } : null),
            }
          : isCounterPricingField
            ? { ...item }
            : { ...item, [field]: value };

        if (!sameDetailPricing) {
          if (field === 'cbm') {
            updatedItem.undyed = value;
          }
          if (field === 'unitBbl') {
            updatedItem.dyed = value;
          }
          if (field === 'alpha' && isolateCargoRatioRows) {
            updatedItem.alpha = value;
          }
        }
        if (field === 'counterDyed') {
          updatedItem.counterDyed = value;
          updatedItem.dyed2 = value;
        }
        if (field === 'counterUndyed') {
          updatedItem.counterUndyed = value;
          updatedItem.undyed2 = value;
        }
        if (field === 'counterAlpha') {
          const alphaResult = strategy.applyCounterAlphaState(
            updatedItem,
            item,
            value,
            rowKey,
            row
          );
          if (alphaResult === null) {
            return item;
          }
        }

        return updatedItem;
      });
      return { ...prev, items: newItems };
    });

    if (!isDeliveryEditable) return;

    if (
      field === 'counterDyed' ||
      field === 'counterUndyed' ||
      field === 'counterAlpha'
    ) {
      if (requiresCargoRatio && field === 'counterAlpha') {
        scheduleCounterCargoRatioAlphaSave({
          ...row,
          counterAlpha: updatedCounterAlpha,
        });
        return;
      }
      scheduleCounterDyedUndyedSave({
        ...row,
        counterDyed: updatedCounterDyed,
        counterUndyed: updatedCounterUndyed,
        counterAlpha: updatedCounterAlpha,
        dyed2: updatedCounterDyed,
        undyed2: updatedCounterUndyed,
        alpha: updatedCounterAlpha,
        alpha2: null,
      });
      return;
    }

    if (usesAlphaField && field === 'alpha') {
      if (requiresCargoRatio && row?.quantityInformationCargoRatioId) {
        scheduleCargoRatioPricingSave({
          ...row,
          alpha: updatedAlpha,
        });
        return;
      }
      scheduleDyedUndyedSave({
        ...row,
        alpha: updatedAlpha,
      });
      return;
    }

    if (!usesAlphaField) {
      scheduleDyedUndyedSave({
        ...row,
        [field]: value,
        dyed: updatedDyed,
        undyed: updatedUndyed,
        cbm: field === 'cbm' ? value : row.cbm,
        unitBbl: field === 'unitBbl' ? value : row.unitBbl,
      });
    }
  };

  const handleCapableChange = async (
    e,
    rowKey,
    quantityInformationDetailId,
    incotermLocationIds
  ) => {
    const row = qtyModeDeliveryIncoterm.items.find(
      item => getRowKey(item) === rowKey
    );
    if (row && hasCounterOffer(row)) return;

    const checked = e.target.checked;
    const detailId = String(quantityInformationDetailId ?? '');
    const isolateCargoRatioRows = requiresCargoRatio;
    const isFobOriginCapable = !!(row && isFobOriginItem(row));
    const syncRows = isolateCargoRatioRows
      ? row
        ? [row]
        : []
      : detailId
        ? qtyModeDeliveryIncoterm.items.filter(
            item => String(item.quantityInformationDetailId ?? '') === detailId
          )
        : row
          ? [row]
          : [];
    const syncKeys = syncRows.map(item => getRowKey(item));
    const previousCapableByKey = Object.fromEntries(
      syncKeys.map(key => [key, capableState[key] || false])
    );
    const previousRowsByKey = Object.fromEntries(
      syncRows.map(item => [getRowKey(item), item])
    );
    const previousItemsSnapshot = qtyModeDeliveryIncoterm.items;

    if (!checked) {
      if (isolateCargoRatioRows && row?.quantityInformationCargoRatioId) {
        const epochKey = `cr-${String(row.quantityInformationCargoRatioId)}`;
        pricingSaveEpochRef.current[epochKey] =
          (pricingSaveEpochRef.current[epochKey] || 0) + 1;
        syncKeys.forEach(key => {
          Object.keys(dyedUndyedTimersRef.current).forEach(timerKey => {
            if (timerKey !== key && !timerKey.startsWith(`${key}-`)) return;
            clearTimeout(dyedUndyedTimersRef.current[timerKey]);
            delete dyedUndyedTimersRef.current[timerKey];
          });
          delete pendingSourcePricingRef.current[key];
        });
      } else {
        invalidateSourcePricingSaves(quantityInformationDetailId, syncKeys);
      }
    }

    if (!isolateCargoRatioRows && isFobOriginCapable) {
      const selectedLocations = checked
        ? (Array.isArray(incotermLocationIds) ? incotermLocationIds : [])
            .map(id => {
              const locId = String(id);
              const fromLocations = (
                Array.isArray(row?.incotermFOBLocations)
                  ? row.incotermFOBLocations
                  : []
              ).find(loc => String(loc?.id ?? loc?.Id ?? '') === locId);
              const fromSelected = (
                Array.isArray(row?.selectedCapableLocations)
                  ? row.selectedCapableLocations
                  : []
              ).find(loc => String(loc?.id ?? '') === locId);
              return {
                id: locId,
                name:
                  fromSelected?.name ||
                  fromLocations?.name ||
                  fromLocations?.Name ||
                  '',
              };
            })
            .filter(loc => loc.id)
        : [];

      const nextItems = rebuildFobOriginCapableRows(
        previousItemsSnapshot,
        detailId,
        selectedLocations,
        checked
      );
      const nextCapableKeys = nextItems
        .filter(
          item => String(item.quantityInformationDetailId ?? '') === detailId
        )
        .map(item => getRowKey(item));

      setCapableState(prev => {
        const next = { ...prev };
        syncKeys.forEach(key => {
          delete next[key];
        });
        nextCapableKeys.forEach(key => {
          next[key] = checked;
        });
        return next;
      });
      setQtyModeDeliveryIncoterm(prev => ({ ...prev, items: nextItems }));

      try {
        await postCheckIsCapable(quantityInformationDetailId, checked);
        if (!checked && quantityInformationDetailId) {
          await putIsCapableLocations(quantityInformationDetailId, []);
          await clearFobOriginLocationPricing(syncRows);
          await clearSourcePricing(quantityInformationDetailId);
        } else if (checked && quantityInformationDetailId) {
          await putIsCapableLocations(
            quantityInformationDetailId,
            selectedLocations.map(loc => loc.id)
          );
        }
      } catch (error) {
        console.error('Error in postCheckIsCapable:', error);
        setCapableState(prev => {
          const next = { ...prev };
          nextCapableKeys.forEach(key => {
            delete next[key];
          });
          return { ...next, ...previousCapableByKey };
        });
        setQtyModeDeliveryIncoterm({ items: previousItemsSnapshot });
      }
      return;
    }

    setCapableState(prev => {
      const next = { ...prev };
      syncKeys.forEach(key => {
        next[key] = checked;
      });
      return next;
    });

    setQtyModeDeliveryIncoterm(prev => {
      const newItems = prev.items.map(item => {
        const matches = isolateCargoRatioRows
          ? getRowKey(item) === rowKey
          : detailId
            ? String(item.quantityInformationDetailId ?? '') === detailId
            : getRowKey(item) === rowKey;

        if (!matches) {
          return item;
        }

        if (!checked) {
          return {
            ...item,
            capable: false,
            alpha: '',
            cbm: '',
            unitBbl: '',
            dyed: '',
            undyed: '',
            selectedCapableLocation: isolateCargoRatioRows
              ? item.selectedCapableLocation
              : null,
            selectedCapableLocations: isolateCargoRatioRows
              ? item.selectedCapableLocations
              : [],
            capableLocationIds: isolateCargoRatioRows
              ? item.capableLocationIds
              : [],
            listIsCapable: isolateCargoRatioRows ? item.listIsCapable : [],
          };
        }
        return {
          ...item,
          capable: true,
          alpha: '',
          cbm: '',
          unitBbl: '',
          dyed: '',
          undyed: '',
        };
      });
      return { ...prev, items: newItems };
    });

    try {
      if (isolateCargoRatioRows) {
        if (checked) {
          await postCheckIsCapable(quantityInformationDetailId, true);
          if (
            quantityInformationDetailId &&
            Array.isArray(incotermLocationIds)
          ) {
            await putIsCapableLocations(
              quantityInformationDetailId,
              incotermLocationIds
            );
          }
        } else {
          if (row?.quantityInformationCargoRatioId) {
            await putCargoRatioPricing({
              quantityInformationCargoRatioId:
                row.quantityInformationCargoRatioId,
              quantityInformationId: row.quantityInformationId,
              quantityIncotermFK: row.quantityIncotermId ?? null,
              cargoRatio: row.cargoRatio,
              parcelSize: row.cargoRatioParcelSize ?? row.parcelSize ?? null,
              alpha: null,
              existing: resolveCargoRatioExistingFromRow(row),
              token,
            });
          }

          const otherCapable = qtyModeDeliveryIncoterm.items.some(item => {
            if (getRowKey(item) === rowKey) return false;
            if (String(item.quantityInformationDetailId ?? '') !== detailId) {
              return false;
            }
            return !!capableState[getRowKey(item)];
          });

          if (!otherCapable && quantityInformationDetailId) {
            await postCheckIsCapable(quantityInformationDetailId, false);
            await putIsCapableLocations(quantityInformationDetailId, []);
          }
        }
        return;
      }

      await postCheckIsCapable(quantityInformationDetailId, checked);
      if (!checked && quantityInformationDetailId) {
        await putIsCapableLocations(quantityInformationDetailId, []);
        await clearSourcePricing(quantityInformationDetailId);
      } else if (
        checked &&
        quantityInformationDetailId &&
        Array.isArray(incotermLocationIds)
      ) {
        await putIsCapableLocations(
          quantityInformationDetailId,
          incotermLocationIds
        );
      }
    } catch (error) {
      console.error('Error in postCheckIsCapable:', error);
      setCapableState(prev => ({
        ...prev,
        ...previousCapableByKey,
      }));
      setQtyModeDeliveryIncoterm(prev => ({
        ...prev,
        items: prev.items.map(item => {
          const key = getRowKey(item);
          return previousRowsByKey[key] || item;
        }),
      }));
    }
  };

  const toggleRowExpand = rowKey => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const handleCounterFormChange = (field, value) => {
    setCounterFormErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setCounterFormData(prev => {
      if (
        field === 'quantityLocation1' ||
        field === 'quantityLocation2' ||
        field === 'quantityLocation3'
      ) {
        const quantities = applyCounterQuantityChange(prev, field, value);
        if (!quantities) return prev;
        return { ...prev, ...quantities };
      }

      const next = { ...prev, [field]: value };

      if (field === 'deliveryBasis') {
        if (
          prev.incotermMultiPortType === '2Port' ||
          prev.incotermMultiPortType === '3Port'
        ) {
          Object.assign(
            next,
            splitDeliveryBasisByMultiport(value, prev.incotermMultiPortType)
          );
        }
      }

      if (field === 'incotermId') {
        next.timelineType = value === FOB_INCOTERM_ID ? 'ALD' : 'ADD';
        next.timelineType2 = null;
        next.timelineType3 = null;
        next.incotermRouteId = '';
        next.incotermRouteLabel = '';
        next.incotermPortType = '';
        next.incotermMultiPortType = '';
        next.incotermFOB = [];
        next.incotermLocationId = '';
        next.incotermLocation2Id = value === FOB_INCOTERM_ID ? [] : '';
        next.incotermLocation3Id = '';
        next.quantityLocation1 = '';
        next.quantityLocation2 = '';
        next.quantityLocation3 = '';
        next.startDate = '';
        next.endDate = '';
        next.startDate2 = '';
        next.endDate2 = '';
        next.startDate3 = '';
        next.endDate3 = '';
      }

      if (field === 'incotermPortType') {
        next.incotermMultiPortType = '';
        next.incotermLocation2Id = '';
        next.incotermLocation3Id = '';
        next.quantityLocation1 = '';
        next.quantityLocation2 = '';
        next.quantityLocation3 = '';
        next.startDate2 = '';
        next.endDate2 = '';
        next.startDate3 = '';
        next.endDate3 = '';
      }

      if (field === 'incotermRouteId') {
        const routeOption = incotermRouteOptions.find(
          option => option.value === value
        );
        next.incotermRouteLabel = routeOption?.label ?? '';
        next.incotermFOB = [];
        next.incotermLocationId = '';
        next.incotermLocation2Id =
          prev.incotermId === FOB_INCOTERM_ID ? [] : '';
        next.incotermLocation3Id = '';
        next.quantityLocation1 = '';
        next.quantityLocation2 = '';
        next.quantityLocation3 = '';
        next.startDate2 = '';
        next.endDate2 = '';
        next.startDate3 = '';
        next.endDate3 = '';
      }

      if (field === 'incotermMultiPortType') {
        Object.assign(
          next,
          splitDeliveryBasisByMultiport(prev.deliveryBasis, value)
        );

        if (value === '2Port') {
          next.incotermLocation3Id = '';
          next.startDate3 = '';
          next.endDate3 = '';
        }

        if (value !== '2Port' && value !== '3Port') {
          next.incotermLocation2Id = '';
          next.incotermLocation3Id = '';
          next.startDate2 = '';
          next.endDate2 = '';
          next.startDate3 = '';
          next.endDate3 = '';
        }
      }

      return next;
    });
  };

  const requestCounter = async (path, options = {}) => {
    const response = await fetch(`${api.API_URL}/${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (response.status === 204) return null;
    const responseText = await response.text();
    return responseText ? JSON.parse(responseText) : null;
  };

  const findCounterByForeignKey = async (endpoint, parameter, foreignKey) => {
    const result = await requestCounter(
      `${endpoint}?${parameter}=${encodeURIComponent(foreignKey)}`
    );
    const data = unwrapApiData(result);
    const items = Array.isArray(data)
      ? data
      : (data?.items ?? data?.Items ?? unwrapApiItems(result));
    return items?.[0] ?? null;
  };

  const upsertCounter = async ({
    endpoint,
    parameter,
    foreignKey,
    createPayload,
    updatePayload,
  }) => {
    const existing = await findCounterByForeignKey(
      endpoint,
      parameter,
      foreignKey
    );
    const id = existing?.id ?? existing?.Id;
    const result = await requestCounter(id ? `${endpoint}/${id}` : endpoint, {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(id ? updatePayload : createPayload),
    });
    const saved = unwrapApiData(result);
    const savedId = saved?.id ?? saved?.Id ?? id;

    if (savedId) return savedId;

    const created = await findCounterByForeignKey(
      endpoint,
      parameter,
      foreignKey
    );
    return created?.id ?? created?.Id;
  };

  const handleSaveCounter = async modalFormData => {
    if (!selectedRowData) {
      closeCounterModal();
      return;
    }

    if (
      !hasCounterFormChanges(modalFormData, counterOriginalFormData, {
        ...strategy.getCounterFormChangeOptions(requiresCargoRatio),
      })
    ) {
      Swal.fire({
        icon: 'warning',
        text: 'Counter data must be different from the original data.',
      });
      return;
    }

    const validationErrors = validateCounterIncoterm(modalFormData, {
      ...strategy.getValidateCounterOptions(requiresCargoRatio),
    });
    if (Object.keys(validationErrors).length) {
      setCounterFormErrors(validationErrors);
      Swal.fire({
        icon: 'warning',
        text: 'Please Check Mandatory Fields..',
      });
      return;
    }

    setCounterFormLoading(true);
    try {
      if (
        !selectedRowData.quantityIncotermId ||
        !selectedRowData.quantityInformationId ||
        !selectedRowData.quantityInformationDetailId
      ) {
        throw new Error('Counter foreign key is incomplete');
      }

      const latestRow =
        qtyModeDeliveryIncoterm.items.find(
          item => getRowKey(item) === getRowKey(selectedRowData)
        ) || selectedRowData;

      const { incotermPayload, quantityInformationPayload, detailPayload } =
        buildCounterSavePayloads({
          modalFormData,
          quantityDeliveryTermId,
          capable: capableState[getRowKey(selectedRowData)] || false,
          counterDyed: latestRow.counterDyed,
          counterUndyed: latestRow.counterUndyed,
          counterAlpha:
            latestRow.counterAlpha ?? latestRow.alpha2 ?? latestRow.alpha,
          preserveQuantity: strategy.preserveQuantityOnSave,
          originalRow: latestRow,
        });

      const quantityIncotermCounterId = await upsertCounter({
        endpoint: 'quantity-incoterm-counter',
        parameter: 'QuantityIncotermFK',
        foreignKey: selectedRowData.quantityIncotermId,
        createPayload: {
          quantityIncotermFK: selectedRowData.quantityIncotermId,
          ...incotermPayload,
        },
        updatePayload: incotermPayload,
      });

      const groupRowsForCounter = strategy.getGroupRowsForCounter(
        qtyModeDeliveryIncoterm.items,
        selectedRowData
      );

      const informationTargets = [
        ...new Map(
          groupRowsForCounter
            .filter(item => item.quantityInformationId)
            .map(item => [String(item.quantityInformationId), item])
        ).values(),
      ];

      const informationCounterIdByQi = new Map();
      for (const infoRow of informationTargets) {
        const informationCounterId = await upsertCounter({
          endpoint: 'quantity-information-counter',
          parameter: 'QuantityInformationFK',
          foreignKey: infoRow.quantityInformationId,
          createPayload: {
            quantityInformationFK: infoRow.quantityInformationId,
            ...quantityInformationPayload,
          },
          updatePayload: quantityInformationPayload,
        });
        if (informationCounterId) {
          informationCounterIdByQi.set(
            String(infoRow.quantityInformationId),
            informationCounterId
          );
        }
      }

      const quantityInformationCounterId =
        informationCounterIdByQi.get(
          String(selectedRowData.quantityInformationId)
        ) || null;

      if (!quantityIncotermCounterId || !quantityInformationCounterId) {
        throw new Error('Counter parent ID was not returned by the API');
      }

      const detailTargets = [
        ...new Map(
          groupRowsForCounter
            .filter(item => item.quantityInformationDetailId)
            .map(item => [String(item.quantityInformationDetailId), item])
        ).values(),
      ];

      for (const detailRow of detailTargets) {
        const infoCounterId =
          informationCounterIdByQi.get(
            String(detailRow.quantityInformationId)
          ) || quantityInformationCounterId;
        const detailAlpha =
          parseNumericQuantity(detailRow.counterAlpha) ?? detailPayload.alpha;
        await upsertCounter({
          endpoint: 'q-information-detail-counter',
          parameter: 'QuantityInformationDetailFK',
          foreignKey: detailRow.quantityInformationDetailId,
          createPayload: {
            quantityInformationDetailFK: detailRow.quantityInformationDetailId,
            quantityInformationCounterId: infoCounterId,
            quantityIncotermCounterId,
            ...detailPayload,
            alpha: detailAlpha,
          },
          updatePayload: {
            quantityInformationCounterId: infoCounterId,
            quantityIncotermCounterId,
            ...detailPayload,
            alpha: detailAlpha,
          },
        });
      }

      if (requiresCargoRatio && strategy.saveCargoRatioCounters) {
        try {
          await strategy.saveCargoRatioCounters({
            modalFormData,
            selectedRowData,
            counterOriginalFormData,
            groupRowsForCounter,
            informationCounterIdByQi,
            quantityIncotermCounterId,
            requestCounter,
            token,
          });
        } catch (cargoRatioError) {
          console.error('Error saving cargo ratio counters:', cargoRatioError);
        }
      }

      const counteredQiIds = new Set(informationCounterIdByQi.keys());
      const counteredDetailIds = new Set(
        detailTargets
          .map(item => item.quantityInformationDetailId)
          .filter(Boolean)
          .map(String)
      );

      const capableRowsToDisable = qtyModeDeliveryIncoterm.items.filter(
        item =>
          counteredQiIds.has(String(item.quantityInformationId ?? '')) ||
          counteredDetailIds.has(String(item.quantityInformationDetailId ?? ''))
      );

      const detailIdsToDisable = [
        ...new Set(
          capableRowsToDisable
            .map(item => item.quantityInformationDetailId)
            .filter(Boolean)
            .map(String)
        ),
      ];

      capableRowsToDisable.forEach(item => {
        const rowKey = getRowKey(item);
        if (dyedUndyedTimersRef.current[rowKey]) {
          clearTimeout(dyedUndyedTimersRef.current[rowKey]);
          delete dyedUndyedTimersRef.current[rowKey];
        }
      });

      const pricingClearByDetailId = new Map();
      capableRowsToDisable.forEach(item => {
        const detailId = item.quantityInformationDetailId;
        if (!detailId || pricingClearByDetailId.has(String(detailId))) return;
        pricingClearByDetailId.set(String(detailId), {
          quantityInformationDetailId: detailId,
          dyed: '',
          undyed: '',
          dyed2: '',
          undyed2: '',
          alpha: '',
          alpha2: '',
        });
      });

      await Promise.all([
        ...detailIdsToDisable.map(detailId =>
          postCheckIsCapable(detailId, false).catch(() => null)
        ),
        ...[...pricingClearByDetailId.values()].map(payload =>
          postSetDyedUndyed(payload).catch(() => null)
        ),
      ]);

      await fetchDeliveryDataDetailIncoterm(
        form.id,
        quantityDeliveryTermId,
        currentPage,
        rowsPerPage,
        token
      );
      setExpandedRows(prev => ({
        ...prev,
        [strategy.getAfterSaveExpandKey(selectedRowData)]: true,
      }));
    } catch (error) {
      console.error('Error saving counter offer:', error);
    } finally {
      closeCounterModal();
      setCounterFormLoading(false);
    }
  };

  const handleDeleteCounter = async rowData => {
    if (!rowData || !isDeliveryEditable) return;
    if (!hasCounterOffer(rowData)) return;

    const confirmResult = await Swal.fire({
      title: 'Are You sure want to Delete this Record?',
      text: 'Deleted Data cannot be Recovered!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    });

    if (!confirmResult.isConfirmed) return;

    setIsLoading(true);
    try {
      const deleteById = async (endpoint, id) => {
        if (!id) return;
        const response = await fetch(`${api.API_URL}/${endpoint}/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      };

      let detailId = rowData.quantityInformationDetailCounterId;
      let informationId = rowData.quantityInformationCounterId;
      let incotermId = rowData.quantityIncotermCounterId;

      if (!detailId || !informationId || !incotermId) {
        const [detail, information, incoterm] = await Promise.all([
          findCounterByForeignKey(
            'q-information-detail-counter',
            'QuantityInformationDetailFK',
            rowData.quantityInformationDetailId
          ),
          findCounterByForeignKey(
            'quantity-information-counter',
            'QuantityInformationFK',
            rowData.quantityInformationId
          ),
          findCounterByForeignKey(
            'quantity-incoterm-counter',
            'QuantityIncotermFK',
            rowData.quantityIncotermId
          ),
        ]);
        detailId = detailId || detail?.id || detail?.Id;
        informationId = informationId || information?.id || information?.Id;
        incotermId = incotermId || incoterm?.id || incoterm?.Id;
      }

      const shouldDeleteSharedCounter = strategy.shouldDeleteSharedCounter({
        rowData,
        items: qtyModeDeliveryIncoterm.items,
      });

      if (strategy.deleteCounterExtras) {
        await strategy.deleteCounterExtras({
          rowData,
          informationId,
          incotermCounterId: incotermId,
          requiresCargoRatio,
          requestCounter,
          findCounterByForeignKey,
          deleteById,
        });
      }

      await deleteById('q-information-detail-counter', detailId);
      if (shouldDeleteSharedCounter) {
        await deleteById('quantity-information-counter', informationId);
      }
      await deleteById('quantity-incoterm-counter', incotermId);

      await fetchDeliveryDataDetailIncoterm(
        form.id,
        quantityDeliveryTermId,
        currentPage,
        rowsPerPage,
        token
      );
    } catch (error) {
      console.error('Error deleting counter offer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openCounterModal = async rowData => {
    setSelectedRowData(rowData);
    setCounterFormErrors({});
    setCounterModalOpen(true);
    setCounterFormLoading(true);

    try {
      let nextForm = buildCounterFormFromRow(
        rowData,
        form,
        incotermTypeOptions
      );
      const originalMultiport = {
        incotermPortType: nextForm.incotermPortType,
        incotermMultiPortType: nextForm.incotermMultiPortType,
      };

      if (rowData?.quantityIncotermId) {
        const detail = await fetchQuantityIncotermDetail(
          rowData.quantityIncotermId
        );
        if (detail) {
          nextForm = mapIncotermDetailToCounterForm(detail, rowData, form, {
            fromCounter: false,
            incotermRouteOptions,
          });
          originalMultiport.incotermPortType = nextForm.incotermPortType;
          originalMultiport.incotermMultiPortType =
            nextForm.incotermMultiPortType;
        }
      }
      const procurementForm = nextForm;

      if (
        rowData.quantityIncotermId &&
        rowData.quantityInformationId &&
        rowData.quantityInformationDetailId
      ) {
        let [incotermCounter, informationCounter, detailCounter] =
          await Promise.all([
            findCounterByForeignKey(
              'quantity-incoterm-counter',
              'QuantityIncotermFK',
              rowData.quantityIncotermId
            ),
            findCounterByForeignKey(
              'quantity-information-counter',
              'QuantityInformationFK',
              rowData.quantityInformationId
            ),
            findCounterByForeignKey(
              'q-information-detail-counter',
              'QuantityInformationDetailFK',
              rowData.quantityInformationDetailId
            ),
          ]);

        const incotermCounterId =
          rowData.quantityIncotermCounterId ||
          incotermCounter?.id ||
          incotermCounter?.Id;
        const informationCounterId =
          rowData.quantityInformationCounterId ||
          informationCounter?.id ||
          informationCounter?.Id;
        const detailCounterId =
          rowData.quantityInformationDetailCounterId ||
          detailCounter?.id ||
          detailCounter?.Id;

        const [incotermFull, informationFull, detailFull] = await Promise.all([
          incotermCounterId
            ? requestCounter(
                `quantity-incoterm-counter/${incotermCounterId}`
              ).then(unwrapApiData)
            : Promise.resolve(incotermCounter),
          informationCounterId
            ? requestCounter(
                `quantity-information-counter/${informationCounterId}`
              ).then(unwrapApiData)
            : Promise.resolve(informationCounter),
          detailCounterId
            ? requestCounter(
                `q-information-detail-counter/${detailCounterId}`
              ).then(unwrapApiData)
            : Promise.resolve(detailCounter),
        ]);

        incotermCounter = incotermFull || incotermCounter;
        informationCounter = informationFull || informationCounter;
        detailCounter = detailFull || detailCounter;

        if (incotermCounter) {
          nextForm = preserveProcurementIncotermFields(
            mapIncotermDetailToCounterForm(
              {
                ...incotermCounter,
                quantityInformationDetail: detailCounter ? [detailCounter] : [],
              },
              {
                ...rowData,
                deliveryBasis:
                  informationCounter?.quantity ??
                  rowData.counterDeliveryBasis ??
                  rowData.deliveryBasis,
              },
              form,
              {
                fromCounter: true,
                incotermRouteOptions,
              }
            ),
            procurementForm
          );

          if (
            !nextForm.incotermPortType &&
            originalMultiport.incotermPortType
          ) {
            nextForm.incotermPortType = originalMultiport.incotermPortType;
            nextForm.incotermMultiPortType =
              originalMultiport.incotermMultiPortType;
          }
        }

        if (requiresCargoRatio) {
          nextForm = await strategy.enrichOpenCounterModal({
            nextForm,
            rowData,
            items: qtyModeDeliveryIncoterm.items,
            informationCounterId,
            requestCounter,
          });
        }
      }

      if (strategy.isLpg) {
        const originalQty =
          rowData.deliveryBasis ?? rowData.parcelSize ?? nextForm.deliveryBasis;
        nextForm.deliveryBasis = originalQty;
        nextForm.quantityLocation1 =
          rowData.parcelSize ?? rowData.deliveryBasis ?? originalQty;
        nextForm.quantityLocation2 = rowData.parcelSize2 ?? '';
        nextForm.quantityLocation3 = rowData.parcelSize3 ?? '';
      }

      if (requiresCargoRatio && !(nextForm.selectedRatios?.length > 0)) {
        nextForm.selectedRatios = collectOriginalCargoRatios(
          qtyModeDeliveryIncoterm.items,
          rowData
        );
      }

      // Ensure route label for FOB Straits/Origin visibility rules
      if (
        nextForm.incotermId === FOB_INCOTERM_ID &&
        nextForm.incotermRouteId &&
        !nextForm.incotermRouteLabel
      ) {
        nextForm.incotermRouteLabel =
          incotermRouteOptions.find(
            option => String(option.value) === String(nextForm.incotermRouteId)
          )?.label ?? '';
      }

      const originalForm = {
        ...procurementForm,
        ...strategy.buildOriginalFormExtras({
          rowData,
          nextForm,
          items: qtyModeDeliveryIncoterm.items,
          requiresCargoRatio,
        }),
      };

      setCounterOriginalFormData(originalForm);
      setCounterFormData(nextForm);

      if (nextForm.incotermId === FOB_INCOTERM_ID) {
        if (nextForm.incotermRouteId) {
          await fetchIncotermLocationOptions(nextForm.incotermRouteId, null);
        }
      } else if (nextForm.incotermId) {
        await fetchIncotermLocationOptions(null, nextForm.incotermId);
      }
    } catch (error) {
      console.error('Error loading counter form data:', error);
      const fallbackForm = buildCounterFormFromRow(
        rowData,
        form,
        incotermTypeOptions
      );
      setCounterOriginalFormData(fallbackForm);
      setCounterFormData(fallbackForm);
    } finally {
      setCounterFormLoading(false);
    }
  };

  const closeCounterModal = () => {
    setCounterModalOpen(false);
    setSelectedRowData(null);
    setCounterFormData(EMPTY_COUNTER_FORM);
    setCounterOriginalFormData(null);
    setCounterFormErrors({});
    setIncotermLocationOptions([]);
  };

  return {
    isLpgProduct,
    usesDyedUndyed,
    usesAlphaField,
    pricingUnit,
    alphaColumnLabel,
    requiresCargoRatio,
    isDeliveryEditable,
    isLoading,
    searchTerm,
    setSearchTerm,
    filteredItems,
    capableState,
    expandedRows,
    counterModalOpen,
    counterFormData,
    counterHasChanges,
    counterFormErrors,
    counterFormLoading,
    incotermTypeOptions,
    incotermRouteOptions,
    incotermLocationOptions,
    fobRouteOptions,
    form,
    handleInputChange,
    handleCapableChange,
    toggleRowExpand,
    handleCounterFormChange,
    handleSaveCounter,
    handleDeleteCounter,
    openCounterModal,
    closeCounterModal,
  };
};

export default useModelDeliveryTableBase;
